class SbTopBar {
  constructor() {
    this.init();
  }

  init() {
    this.cacheDom();
    this.events();

    this.syncPrice();
    this.updateSticky();
  }

  cacheDom() {
    this.wrapper = document.querySelector('.sb-top-bar-wrapper');
    this.topBar = document.querySelector('.sb-top-bar');
    this.header = document.querySelector('.cc-header') || document.querySelector('.header');
    this.productDetails = document.querySelector('.product-info');
  }

  events() {
    window.addEventListener('scroll', this.requestUpdate.bind(this), { passive: true });
    window.addEventListener('resize', this.requestUpdate.bind(this), { passive: true });
    window.addEventListener('load', this.requestUpdate.bind(this));

    // The header collapses its menu row on scroll, so follow its actual height.
    if (window.ResizeObserver) {
      const observer = new ResizeObserver(this.requestUpdate.bind(this));
      if (this.header) observer.observe(this.header);
      if (this.topBar) observer.observe(this.topBar);
    }

    // Keep the price in sync with the product info block.
    document.addEventListener('on:variant:change', () => {
      this.syncPrice();
      setTimeout(this.syncPrice.bind(this), 100);
    });

    // The product info is re-rendered on variant change, so listen on document.
    document.addEventListener('click', (evt) => {
      if (evt.target.closest('#sb-back-button')) {
        this.scrollToTop();
        return;
      }

      if (evt.target.closest('#st-back-button')) {
        this.scrollToForm();
        return;
      }

      const cartButton = evt.target.closest('#sb-cart-button, #st-cart-button');
      if (cartButton) {
        evt.preventDefault();
        this.addToCart(cartButton);
      }
    });
  }

  /**
   * The main "Add to Cart" button, re-queried because it gets replaced on variant change.
   * @returns {?Element}
   */
  get mainCartButton() {
    return document.querySelector('.product-info__add-button button')
      || document.querySelector('.js-product-form-main [name="add"]');
  }

  /**
   * Whether a button is currently unusable.
   * @param {Element} button - Button to check.
   * @returns {boolean}
   */
  static isDisabled(button) {
    return button.disabled || button.getAttribute('aria-disabled') === 'true';
  }

  /**
   * Rebuilds the compact price (savings + RRP next to the current price) from the
   * product info price block, so the bars stay correct after a variant change.
   */
  syncPrice() {
    const source = document.querySelector('.product-info__price .price');
    if (!source) return;

    const current = source.querySelector('.price__current');
    if (!current) return;

    const was = source.querySelector('.price__was');
    const percent = source.querySelector('.price__savings-percent');
    const hasUvpLabel = source.querySelector('.price__uvp__label') !== null;
    const wasText = was ? was.textContent.trim() : '';
    const percentText = percent ? percent.textContent.replace('-', '').trim() : '';

    let meta = '';
    if (wasText) {
      meta = '<div class="sb-price__meta">'
        + (percentText ? `<span class="sb-price__save">Sie sparen ${percentText}</span>` : '')
        + `<s class="sb-price__was">${hasUvpLabel ? 'UVP ' : ''}${wasText}</s>`
        + '</div>';
    }

    const html = `${meta}<strong class="sb-price__current">${current.innerHTML}</strong>`;

    document.querySelectorAll('.js-sb-price .sb-price').forEach((el) => {
      el.innerHTML = html;
    });
  }

  /**
   * Throttles sticky updates to one per frame.
   */
  requestUpdate() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.ticking = false;
      this.updateSticky();
    });
  }

  /**
   * Pins the bar under the header once its static position is scrolled past.
   */
  updateSticky() {
    if (!this.wrapper || !this.topBar) return;

    const headerBottom = this.header
      ? Math.max(0, this.header.getBoundingClientRect().bottom)
      : 0;

    const height = this.topBar.offsetHeight;
    if (height) this.wrapper.style.setProperty('--sb-top-bar-height', `${height}px`);

    const wrapperTop = this.wrapper.getBoundingClientRect().top + window.scrollY;
    const isStuck = window.scrollY + headerBottom > wrapperTop;

    this.wrapper.classList.toggle('is-stuck', isStuck);
    if (isStuck) this.wrapper.style.setProperty('--sb-top-bar-offset', `${headerBottom}px`);
  }

  /**
   * Adds the product to the cart, using the selected variant when there is one,
   * otherwise pre-selecting the first available variant.
   * @param {Element} stickyButton - The sticky bar button that was clicked.
   */
  addToCart(stickyButton) {
    if (stickyButton && stickyButton.classList.contains('is-loading')) return;

    const button = this.mainCartButton;
    if (!button) return;

    if (!SbTopBar.isDisabled(button)) {
      this.setLoading(stickyButton, true);
      button.click();
      this.mirrorLoadingState(stickyButton, button);
      return;
    }

    // Nothing selected yet - select the first available variant, then add it.
    if (this.selectFirstAvailableVariant()) {
      this.setLoading(stickyButton, true);
      this.addWhenReady(stickyButton);
    }
  }

  /**
   * Toggles the loading state of a sticky bar button.
   * @param {Element} button - Button to update.
   * @param {boolean} isLoading - Whether the button is loading.
   */
  setLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('is-loading', isLoading);
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (!isLoading) button.classList.remove('is-success');
  }

  /**
   * Keeps the sticky bar button in sync with the main "Add to Cart" button,
   * so it shows the same spinner and success tick.
   * @param {Element} stickyButton - Button to update.
   * @param {Element} mainButton - Main add to cart button to observe.
   */
  mirrorLoadingState(stickyButton, mainButton) {
    if (!stickyButton || !mainButton) return;

    const stop = () => {
      observer.disconnect();
      clearTimeout(giveUp);
      this.setLoading(stickyButton, false);
    };

    const observer = new MutationObserver(() => {
      const loading = mainButton.classList.contains('is-loading');
      const success = mainButton.classList.contains('is-success');

      if (!loading && !success) {
        stop();
        return;
      }

      stickyButton.classList.toggle('is-loading', loading);
      stickyButton.classList.toggle('is-success', success);
    });

    observer.observe(mainButton, { attributes: true, attributeFilter: ['class'] });
    const giveUp = setTimeout(stop, 10000);
  }

  /**
   * Option values of the first available variant.
   * @returns {Array}
   */
  getFirstAvailableOptions() {
    const source = document.querySelector('[data-first-available-options]');
    try {
      const options = JSON.parse(source?.dataset.firstAvailableOptions || '[]');
      return Array.isArray(options) ? options : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Selects the options of the first available variant in the variant picker.
   * @returns {boolean} Whether a selection was made.
   */
  selectFirstAvailableVariant() {
    const picker = document.querySelector('.product-info variant-picker')
      || document.querySelector('variant-picker');
    if (!picker) return false;

    const values = this.getFirstAvailableOptions();
    if (values.length === 0) return false;

    let changed = false;

    picker.querySelectorAll('.option-selector').forEach((selector, selectorIndex) => {
      const index = parseInt(selector.dataset.index, 10);
      const value = values[Number.isNaN(index) ? selectorIndex : index - 1];
      if (typeof value !== 'string') return;

      if (selector.dataset.selectorType === 'dropdown') {
        const customSelect = selector.querySelector('custom-select');
        const option = [...selector.querySelectorAll('.custom-select__option')]
          .find((el) => el.dataset.value === value);

        if (customSelect && option && option.getAttribute('aria-selected') !== 'true') {
          customSelect.selectOption(option);
          changed = true;
        }
      } else {
        const input = [...selector.querySelectorAll('.js-option')].find((el) => el.value === value);
        if (input && !input.checked) {
          input.click();
          changed = true;
        }
      }
    });

    return changed;
  }

  /**
   * Waits for the variant picker to finish updating, then adds to cart.
   * @param {Element} stickyButton - The sticky bar button that was clicked.
   */
  addWhenReady(stickyButton) {
    if (this.pendingAdd) return;
    this.pendingAdd = true;

    const stop = () => {
      this.pendingAdd = false;
      clearInterval(poll);
      clearTimeout(giveUp);
    };

    const attempt = () => {
      const button = this.mainCartButton;
      if (button && !SbTopBar.isDisabled(button)) {
        stop();
        button.click();
        this.mirrorLoadingState(stickyButton, button);
      }
    };

    const poll = setInterval(attempt, 100);
    const giveUp = setTimeout(() => {
      stop();
      this.setLoading(stickyButton, false);
    }, 5000);
  }

  scrollToTop() {
    document.body.scrollIntoView({ behavior: 'smooth' });
  }

  scrollToForm() {
    if (this.productDetails) this.productDetails.scrollIntoView({ behavior: 'smooth' });
  }
}

const sbTopBar = new SbTopBar();
